import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	useCallback,
	useMemo,
	useRef,
	RefObject
} from "react";
import { KnowledgeItemDisplayInfoFormModel } from "./common";
import {
	DimensionRecommendationKind,
	FormItem,
	initialMinimalEditorValue,
	RichTextEditor,
	RichTextEditorIntermediateStateHandlers,
	RichTextEditorToolbarFeature,
	useRichTextEditorIntermediateState,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ImageSelect } from "client/Features/Hub/ImageSelect/ImageSelect";
import {
	getFileMetadata,
	resolveFormattedTextString,
	ensureNotNil
} from "client/Misc/utils";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";
import { AlertManager, useAlerts } from "client/Hooks";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { getKnowledgeItemCategoryImageUrl } from "../../common";
import { userMentionType } from "client/Components/MentionInput/converters";
import { DropOverMentionSuggestionBox } from "../../MentionSuggestionBox/DropOverMentionSuggestionBox";
import { findUsers } from "client/Api/Hub/hub";
import { generateUserDetailsUrl } from "client/Features/Hub/Builder/misc";

export type KnowledgeItemFormProps = {
	attachable: IValueInputAttachable<KnowledgeItemDisplayInfoFormModel>;
	imageId: string | null;
	supportedImageTypes: readonly string[];
	maxImageSize: number;
};

export const KnowledgeItemForm = (props: KnowledgeItemFormProps): ReactElement => {
	const {
		attachable,
		imageId,
		maxImageSize,
		supportedImageTypes
	} = props;

	const alertManager: AlertManager = useAlerts();
	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const editorHandlers: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState(
		initialMinimalEditorValue
	);

	const pageContentAttachable: IValueInputAttachable<string | null> = attachable
		.for("pageContent");

	const imageAttachable: IValueInputAttachable<OptionalModel<Base64FileUriUploadModel | null>> = attachable.for("image");
	const image: OptionalModel<Base64FileUriUploadModel | null> = imageAttachable.value;

	const imageSrc = useMemo(
		(): string | null => {
			if (image.isSet) {
				return image.value
					? image
						.value
						.uri
					: null;
			}

			if (imageId) {
				return getKnowledgeItemCategoryImageUrl(imageId);
			}

			return null;
		},
		[
			image,
			imageId
		]
	);

	const imageChangeHandler = useCallback(
		(image: Base64FileUriUploadWithMetadataModel | null): void => {
			imageAttachable
				.reducer
				.setValue({
					isSet: true,
					value: image
				});

			imageAttachable
				.validation
				.clearServerErrors();

			imageAttachable
				.validation
				.triggerInputFinished();

			if (image && getFileMetadata(image)
				.size > maxImageSize
			) {
				alertManager.showError(
					resolveFormattedTextString({
						template: localize("_MaxSizeIs"),
						replacements: [
							{
								what: "{0}",
								filler: getDisplayFriendlySize(maxImageSize)
							}
						]
					})
				);
			}
		},
		[
			alertManager,
			maxImageSize,
			imageAttachable
		]
	);

	return (
		<>
			<FormItem label={localize("_PageContent")}>
				<div ref={containerRef}>
					<RichTextEditor
						{...pageContentAttachable.attachToRichTextEditor(editorHandlers)}
						supportedFeatures={RICH_TEXT_EDITOR_SUPPORTED_FEATURES}
						areHashUrlsAllowed
						mentionTypes={{
							[userMentionType]: {
								kind: "insert-anchor",
								hrefPattern: generateUserDetailsUrl
							}
						}}
						renderSuggestionBox={
							(props, ref, rect) => (
								<DropOverMentionSuggestionBox
									{...props}
									ref={ref}
									target={ensureNotNil(containerRef.current)}
									findUsers={findUsers}
									rect={rect}
								/>
							)
						}
					/>
				</div>
				<ValidationError {...pageContentAttachable.attachToValidationError()} />
			</FormItem>
			<FormItem label={localize("_Image")}>
				<ImageSelect
					trackingId="select-image"
					src={imageSrc}
					onChange={imageChangeHandler}
					maxImageSize={maxImageSize}
					supportedFormats={supportedImageTypes}
					hasError={!imageAttachable.validation.isValid()}
					dimensionRecommendation={{
						kind: DimensionRecommendationKind.DimensionWithAspectRatio,
						height: 300,
						width: 600,
						aspectRatio: "2:1"
					}}
					supportedFormatsText={[
						localize("_FileExtensionJpg"),
						localize("_FileExtensionPng"),
						localize("_FileExtensionGif")
					]}
				/>
				<ValidationError {...imageAttachable.attachToValidationError()} />
			</FormItem>
		</>
	);
};
KnowledgeItemForm.displayName = "KnowledgeItemForm";

const RICH_TEXT_EDITOR_SUPPORTED_FEATURES: readonly RichTextEditorToolbarFeature[] = [
	"basic-tools",
	"link-tool",
	"font-color-tool",
	"eraser-tool",
	"list-tools"
];